// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useRateCardsData } from "./hooks/useRateCardsData";
import { useLoading } from "../../utils/hooks/useLoading";
import { useToasts } from "../../utils/hooks/useToasts";
import { useUploadDialog } from "../../components/dialogs/dialogs/upload";
import { usePermissions } from "../../utils/permissions";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
import { useToastNotification } from "../../components/ToastNotifications";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { RateCardsGrid } from "./components/grid";
import { RateCardsSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
import { UploadTasksDrawer } from "./components/drawers/upload-tasks";
import { CreateRateCardDrawer } from "./components/drawers/rate-card-drawer";
// --- Utilities
import { tapApi } from "../../api";
import { filterCriteria } from "../../utils/filterCriteria";
import { getAllItemsRange, getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import {
  exportRateCardsToExcel,
  fetchFullRateCardsForExport,
  transformRateCardsForExport,
} from "./utils/rateCardExport";
import { startJobStatusTracker } from "../../utils/jobStatusTracker";
import { validateFiles } from "../../utils/fileValidation";
// --- Styles
import "./styles.scss";

/**
 * Rate Cards Page
 *
 * Features:
 * - Displays a paginated list of rate cards
 * - Provides search, export, upload, and create functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {JSX.Element}
 */
export const RateCardsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    fetchRateCards,
    updateRateCards,
    itemsPerPage,
    rateCardsRange,
    rateCardsSort,
    filterForm,
  } = useRateCardsData();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { openUploadDialog } = useUploadDialog();
  const { createJobProgressToastHandler } = useToastNotification();
  const { isTapAdmin } = usePermissions();

  const { totalPages, currentPage, totalItems, pageSizeOptions } = useSelector(
    (s) => s.rateCards,
  );

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.rateCards,
    itemsPerPage,
    rangeKey: "rateCardsRange",
    onSync: updateRateCards,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchRateCards(nextPageRange);

    if (data) {
      updateRateCards({
        rateCards: data?.content,
        totalPages: data?.totalPages,
        currentPage: data?.pageable?.pageNumber,
        rateCardsRange: nextPageRange,
        totalItems: data?.totalElements,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleExport = async () => {
    if (!totalItems) {
      return;
    }

    setLoadingTrue();
    try {
      const listData = await tapApi.rateCards(
        filterCriteria(filterForm, "", "rateCards"),
        getAllItemsRange(totalItems),
        rateCardsSort,
      );

      if (listData?.code || listData?.httpCode || listData?.error) {
        showErrorToast(
          listData?.message ||
            listData?.error?.message ||
            "Failed to fetch rate cards for export.",
        );
        return;
      }

      const ids = listData?.content?.map((row) => row.id) ?? [];

      if (ids.length === 0) {
        showErrorToast("No rate cards to export.");
        return;
      }

      const rateCards = await fetchFullRateCardsForExport(
        ids,
        tapApi.rateCardById,
      );
      const flatRows = transformRateCardsForExport(rateCards);
      if (flatRows.length === 0) {
        showErrorToast("No rate card data to export.");
        return;
      }

      await exportRateCardsToExcel(flatRows, "ratecards");
    } catch (error) {
      showErrorToast("Error exporting rate cards.");
      console.error("Error exporting rate cards:", error);
    } finally {
      setLoadingFalse();
    }
  };

  const onOpenUploadDialog = (dialogData) => {
    openUploadDialog(
      dialogData,
      async ({ file }) => {
        if (file) {
          const files = Array.isArray(file) ? file : [file];
          const validation = validateFiles(files);
          if (!validation.valid) {
            console.error("File validation failed:", validation.error);
            alert(validation.error);
            return;
          }

          const formData = new FormData();
          files.forEach((f) => formData.append("file", f));

          try {
            setLoadingTrue();
            const jobId = await tapApi.uploadRateCard(formData);

            if (jobId?.code || jobId?.httpCode || jobId?.error) {
              showErrorToast(
                jobId?.message ||
                  jobId?.error?.message ||
                  "Failed to upload rate card.",
              );
              setLoadingFalse();
              return;
            }

            const onJobProgress = createJobProgressToastHandler(jobId);

            startJobStatusTracker(
              jobId,
              async (message, status) => {
                onJobProgress(message, status);
                if (status === "success") {
                  const refreshData = await tapApi.rateCards(
                    filterCriteria(filterForm, "", "rateCards"),
                    rateCardsRange,
                    rateCardsSort,
                  );
                  updateRateCards({
                    rateCards: refreshData?.content,
                    totalPages: refreshData?.totalPages,
                    currentPage: refreshData?.pageable?.pageNumber,
                    totalItems: refreshData?.totalElements,
                  });
                }
                setLoadingFalse();
              },
              (jobTasks, jobInfo) => {
                if (
                  jobInfo?.jobStatus === "COMPLETE" &&
                  (jobInfo?.errorCount ?? 0) === 0
                ) {
                  return;
                }
                updateRateCards({
                  isUploadTasksDrawerOpen: true,
                  rateCardJobTasks: jobTasks || [],
                });
              },
            );

            setLoadingFalse();
          } catch (error) {
            console.error("Error uploading rate card:", error);
            setLoadingFalse();
          }
        }
      },
      "rateCard",
    );
  };

  const handleCreateRateCard = async () => {
    try {
      setLoadingTrue();
      const [currency, format, windowSequence] = await Promise.all([
        tapApi.currency(),
        tapApi.format(),
        tapApi.windowSequence(),
      ]);

      updateRateCards({
        currencyOptions: currency || [],
        formatOptions: format || [],
        windowSequenceOptions: windowSequence || [],
        isCreateDrawerOpen: true,
        isEditModalOpen: false,
        isViewModalOpen: false,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to load rate card options.");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      <Row className="rate-cards-page-header">
        <Col>
          <Typography variant="T50" style={{ marginLeft: 20 }}>
            Rate Cards
          </Typography>
        </Col>
        <Col className="rate-cards-page-header-actions">
          {isTapAdmin && (
            <Button
              iconType="fa-solid fa-upload"
              labelText="Upload"
              styleType="link"
              onClick={onOpenUploadDialog}
              sizeType={"large"}
            >
              Upload
            </Button>
          )}
          {isTapAdmin && (
            <Button
              iconType="fa-solid fa-plus"
              labelText="Create"
              styleType="link"
              onClick={handleCreateRateCard}
            >
              Create
            </Button>
          )}
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={() => updateRateCards({ refreshRateCards: true })}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <RateCardsSearchForm />
        </main>
        <main data-position="bottom">
          <RateCardsGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages || 0}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <CreateRateCardDrawer />
      <UploadTasksDrawer onOpenUploadDialog={onOpenUploadDialog} />
      <ToastNotificationProvider />
    </>
  );
};
